// FeedbackPanel — 19BM-F · progressive subjective-feedback surface.
//
// 陛下要的是"轻量和聚焦一起有"。本组件用单一状态机实现: 同一组件三态切换，而不是
// 两个并行组件 (两个意味着两个挂载点 + 两套状态机 + 两套快捷键逻辑)。
//
// 三态:
//   closed    — 父组件控制 (open=false)，不渲染
//   collapsed — 轻量态: 右下角小卡片，4 个类别 chip (点即秒提交) + "写详情"展开链接
//   expanded  — 聚焦态: textarea 自动 focus，可选填备注 ≤500 字，Cmd+Enter 提交
//
// 转换:
//   collapsed --点"写详情"--> expanded
//   expanded  --Esc/收起----> collapsed
//   任一态    --提交/外部关闭--> closed (父卸载)
//
// 提交走 submit-feedback (30s viewPath 去重 + recordEvent → D1 admin feed)。
// 类别秒提交 (collapsed 态点 chip) 是轻量路径: 不需要打字，一击即报。

import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageSquareText, X, ChevronUp, Send } from 'lucide-react';
import { useT } from '../i18n/index.js';
import { useToast } from './toast.js';
import { useFeedbackKeyboard } from '../lib/hooks/use-feedback-keyboard.js';
import {
  submitFeedback,
  __resetFeedbackDedupForTests,
  type FeedbackCategory,
  type SubmitFeedbackResult,
} from '../lib/submit-feedback.js';

export interface FeedbackPanelProps {
  /** When true, the panel renders. Parent owns this (toggled by the hotkey). */
  open: boolean;
  /** Called when the user dismisses (X click, Esc, or after submit). */
  onClose: () => void;
  /** Current URL pathname — captured at open time so the user doesn't hand-enter it. */
  viewPath: string;
}

const CATEGORIES: ReadonlyArray<{ key: FeedbackCategory; labelKey: 'feedback.categoryOff' | 'feedback.categoryMissing' | 'feedback.categorySlow' | 'feedback.categoryOther' }> = [
  { key: 'off', labelKey: 'feedback.categoryOff' },
  { key: 'missing', labelKey: 'feedback.categoryMissing' },
  { key: 'slow', labelKey: 'feedback.categorySlow' },
  { key: 'other', labelKey: 'feedback.categoryOther' },
];

export function FeedbackPanel({ open, onClose, viewPath }: FeedbackPanelProps) {
  const t = useT();
  const toast = useToast();
  const [mode, setMode] = useState<'collapsed' | 'expanded'>('collapsed');
  const [category, setCategory] = useState<FeedbackCategory | null>(null);
  const [note, setNote] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Reset to collapsed + empty when (re)opened.
  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reset panel to collapsed + empty when (re)opened; key-reset alternative needs parent to own the open prop
      setMode('collapsed');
      setCategory(null);
      setNote('');
    }
  }, [open]);

  // Auto-focus the textarea when entering expanded mode.
  useEffect(() => {
    if (mode === 'expanded' && open) {
      // rAF avoids a focus race with the CSS transition.
      // Session 3 (HIGH-1 audit): cancel pending rAF on cleanup so a fast
      // Esc-close (component still mounted, mode flipped) can't focus a
      // hidden textarea after the transition.
      const handle = requestAnimationFrame(() => textareaRef.current?.focus());
      return () => cancelAnimationFrame(handle);
    }
  }, [mode, open]);

  if (!open) return null;

  async function quickSubmit(cat: FeedbackCategory): Promise<void> {
    const result = await submitFeedback({ viewPath, category: cat });
    showToastForResult(result);
    onClose();
  }

  async function detailedSubmit(): Promise<void> {
    // No category picked in expanded mode — default to 'other' so the note
    // isn't orphaned. The user explicitly chose to write details.
    const cat: FeedbackCategory = category ?? 'other';
    const result = await submitFeedback({ viewPath, category: cat, note: note || undefined });
    showToastForResult(result);
    onClose();
  }

  /** Session 3 (HIGH-2 audit): branch on the 3-way result instead of the old
   *  boolean. 'failed' now surfaces an honest retry prompt instead of lying
   *  "已记录" when IDB write actually threw. */
  function showToastForResult(result: SubmitFeedbackResult): void {
    if (result === 'ok') {
      toast.success(t('feedback.submitted'));
    } else if (result === 'duplicate') {
      toast.warn(t('feedback.duplicate'));
    } else {
      toast.error(t('feedback.failed'));
    }
  }

  function onKeyDown(e: React.KeyboardEvent): void {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      void detailedSubmit();
    }
  }

  return (
    <div
      className={`feedback-panel feedback-panel-${mode}`}
      role="dialog"
      aria-label={t('feedback.title')}
      onKeyDown={onKeyDown}
    >
      <header className="feedback-panel-head">
        <span className="feedback-panel-title">
          <MessageSquareText size={14} aria-hidden="true" />
          {t('feedback.title')}
        </span>
        <button
          type="button"
          className="feedback-panel-close"
          aria-label={t('common.close')}
          onClick={onClose}
        >
          <X size={14} aria-hidden="true" />
        </button>
      </header>

      <p className="feedback-panel-subtitle">{t('feedback.subtitle')}</p>

      <div className="feedback-panel-categories">
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`feedback-chip${category === c.key ? ' is-active' : ''}`}
            // Collapsed mode: chip click = instant submit (lightweight path).
            // Expanded mode: chip click = just select (so the note can accompany it).
            onClick={mode === 'collapsed' ? () => { void quickSubmit(c.key); } : () => setCategory(c.key)}
            aria-pressed={category === c.key}
          >
            {t(c.labelKey)}
          </button>
        ))}
      </div>

      {mode === 'expanded' && (
        <>
          <label className="feedback-panel-note-label">{t('feedback.noteLabel')}</label>
          <textarea
            ref={textareaRef}
            className="feedback-panel-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('feedback.notePlaceholder')}
            maxLength={500}
            rows={4}
          />
          <div className="feedback-panel-actions">
            <button
              type="button"
              className="btn-secondary feedback-panel-btn"
              onClick={() => { void detailedSubmit(); }}
            >
              <Send size={13} aria-hidden="true" />
              {t('feedback.submit')}
            </button>
            <button
              type="button"
              className="feedback-panel-collapse"
              onClick={() => setMode('collapsed')}
            >
              <ChevronUp size={12} aria-hidden="true" />
              {t('feedback.collapse')}
            </button>
          </div>
        </>
      )}

      {mode === 'collapsed' && (
        <button
          type="button"
          className="feedback-panel-expand"
          onClick={() => setMode('expanded')}
        >
          {t('feedback.expand')}
        </button>
      )}
    </div>
  );
}

// Re-export test helper so component tests can reset dedup without importing
// from two places.
export { __resetFeedbackDedupForTests };

/**
 * FeedbackPanelHost — the single mount point. Owns the open state + hotkey
 * binding + viewPath capture. Mount this once inside <BrowserRouter> + <ToastProvider>
 * at the App root so the hotkey works on every route (including /course/* which
 * doesn't render MainLayout).
 *
 * viewPath is captured at OPEN time (not render time) so navigating while the
 * panel is open doesn't rewrite the reported location.
 */
export function FeedbackPanelHost() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  // Capture pathname when opening — a ref so the hotkey closure reads the
  // latest without re-binding.
  const viewPathRef = useRef(location.pathname);
  viewPathRef.current = location.pathname;

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  useFeedbackKeyboard(open, handleToggle);

  return (
    <FeedbackPanel
      open={open}
      onClose={() => setOpen(false)}
      viewPath={viewPathRef.current}
    />
  );
}
